import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BadgeCourier from '@/domains/dashboard-management/components/BadgeCourier/BadgeCourier';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import PhotoUploader, {
  type PhotoItem,
} from '@/domains/dashboard-management/components/PhotoUploader/PhotoUploader';
import {
  useAdminProduct,
  useAdminProductCategories,
  useDeleteAdminProduct,
  useSaveAdminProduct,
} from '@/domains/dashboard-management/hooks/useAdminProducts';
import type {
  AdminProductBody,
  AdminProductDetail,
  AdminProductStatus,
} from '@/domains/dashboard-management/types/adminProduct.types';
import {
  PRODUCT_STATUS_BADGE,
  PRODUCT_STATUS_OPTIONS,
} from '@/domains/dashboard-management/utils/productStatus';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import BackAction from '@/shared/components/ui/BackAction/BackAction';
import Breadcrumb from '@/shared/components/ui/Breadcrumb/Breadcrumb';
import Button from '@/shared/components/ui/Button';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import LogList from '@/shared/components/ui/LogList/LogList';
import { formatDateToMMDDYYYY, formatTimeOnly } from '@/shared/utils/date';
import { showToast } from '@/shared/utils/showToast';

interface ProductDetailState {
  code?: string;
}

interface FormState {
  title: string;
  language: string;
  isbn: string;
  category_code: string;
  author: string;
  description: string;
  length_cm: string;
  width_cm: string;
  thickness_cm: string;
  weight_grams: string;
  /** digits only; shown with thousands separators */
  price: string;
  discount_percent: string;
  stock: string;
  status: AdminProductStatus | '';
}

const EMPTY_FORM: FormState = {
  title: '',
  language: '',
  isbn: '',
  category_code: '',
  author: '',
  description: '',
  length_cm: '',
  width_cm: '',
  thickness_cm: '',
  weight_grams: '',
  price: '',
  discount_percent: '',
  stock: '',
  status: '',
};

const MAX_PHOTOS = 35;
const PHOTO_TYPES = 'image/jpeg,image/png,image/webp';

const digits = (value: string) => value.replace(/\D/g, '');
/** "23,4" (Indonesian decimal comma) -> "23.4" */
const decimal = (value: string) => value.trim().replace(',', '.');
/** Drops trailing zeros of an API decimal: "23.40" -> "23.4", "50.00" -> "50" */
const plain = (value: string | null) =>
  value === null ? '' : String(Number(value)).replace('.', ',');

const formFrom = (product: AdminProductDetail): FormState => ({
  title: product.title,
  language: product.language ?? '',
  isbn: product.isbn ?? '',
  category_code: product.category?.code ?? '',
  author: product.author ?? '',
  description: product.description ?? '',
  length_cm: plain(product.length_cm),
  width_cm: plain(product.width_cm),
  thickness_cm: plain(product.thickness_cm),
  weight_grams: product.weight_grams === null ? '' : String(product.weight_grams),
  price: String(Math.round(Number(product.price))),
  discount_percent: Number(product.discount_percent) === 0 ? '' : plain(product.discount_percent),
  stock: String(product.stock.available),
  status: product.status,
});

/** The API's rounding: half-up to whole rupiah. */
const finalPrice = (price: string, discount: string) => {
  const base = Number(price || 0);
  const percent = Number(decimal(discount) || 0);
  if (!Number.isFinite(percent)) return base;
  return Math.round((base * (100 - percent)) / 100);
};

const formatStamp = (value: string) =>
  `${formatDateToMMDDYYYY(value).replace(/\//g, '-')} | ${formatTimeOnly(value)}`;

const ProductDetailManagement = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('id') ?? undefined;
  const isEdit = Boolean(productId);

  const { data: product, isLoading, isError } = useAdminProduct(productId);
  const { data: categories } = useAdminProductCategories();
  const saveProduct = useSaveAdminProduct();
  const deleteProduct = useDeleteAdminProduct();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const photosRef = useRef<PhotoItem[]>([]);
  photosRef.current = photos;

  const isBundle = product?.product_type === 'bundle';
  const code = product?.sku ?? (state as ProductDetailState | null)?.code ?? '';
  const busy = saveProduct.isPending || deleteProduct.isPending;

  // The form and photos follow the saved product (also after Simpan refetches it)
  useEffect(() => {
    if (!product) return;
    setForm(formFrom(product));
    setPhotos((prev) => {
      for (const p of prev) if (p.file) URL.revokeObjectURL(p.url);
      return product.images
        .filter((image) => image.url)
        .map((image) => ({ id: image.id, url: image.url as string }));
    });
    setRemovedImageIds([]);
  }, [product]);

  useEffect(() => {
    return () => {
      for (const p of photosRef.current) if (p.file) URL.revokeObjectURL(p.url);
    };
  }, []);

  const set =
    (field: keyof FormState, clean: (value: string) => string = (v) => v) =>
    (e: { target: { value: string } }) =>
      setForm((prev) => ({ ...prev, [field]: clean(e.target.value) }));

  const handleAddPhotos = useCallback((files: File[]) => {
    const next = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${crypto.randomUUID()}`,
      url: URL.createObjectURL(file),
      file,
    }));
    setPhotos((prev) => [...prev, ...next]);
  }, []);

  // A new photo is just dropped; a saved one is deleted on Simpan
  const handleRemovePhoto = useCallback((id: string) => {
    const target = photosRef.current.find((p) => p.id === id);
    if (!target) return;
    if (target.file) URL.revokeObjectURL(target.url);
    else setRemovedImageIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const body = (): AdminProductBody => ({
    title: form.title.trim(),
    language: form.language.trim() || null,
    isbn: form.isbn.trim() || null,
    category_code: form.category_code || null,
    author: form.author.trim() || null,
    description: form.description.trim() || null,
    length_cm: decimal(form.length_cm),
    width_cm: decimal(form.width_cm),
    thickness_cm: decimal(form.thickness_cm),
    weight_grams: Number(form.weight_grams || 0),
    price: form.price,
    discount_percent: decimal(form.discount_percent) || '0',
    stock: form.stock === '' ? null : Number(form.stock),
    status: form.status as AdminProductStatus,
  });

  const handleSave = () => {
    if (!form.status) {
      showToast({ type: 'warning', title: 'Status wajib diisi', message: 'Pilih status produk.' });
      return;
    }
    saveProduct.mutate(
      {
        productId,
        body: body(),
        newFiles: photos.filter((p) => p.file).map((p) => p.file as File),
        removedImageIds,
      },
      {
        onSuccess: ({ id, photoError }) => {
          showToast({
            type: 'success',
            title: isEdit ? 'Produk disimpan' : 'Produk ditambahkan',
            message: isEdit
              ? 'Perubahan produk berhasil disimpan.'
              : 'Produk baru berhasil ditambahkan.',
          });
          if (photoError) {
            showToast({ type: 'warning', title: 'Foto belum tersimpan', message: photoError });
          }
          if (!isEdit) navigate(`/dashboard/product-detail?id=${id}`, { replace: true });
        },
        onError: (error: Error) => {
          showToast({ type: 'error', title: 'Gagal menyimpan', message: error.message });
        },
      }
    );
  };

  const handleDelete = () => {
    if (!productId) return;
    deleteProduct.mutate(productId, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Produk dihapus',
          message: 'Produk berhasil dihapus.',
        });
        navigate('/dashboard/product');
      },
      onError: (error: Error) => {
        setConfirmDelete(false);
        showToast({ type: 'error', title: 'Gagal menghapus', message: error.message });
      },
    });
  };

  const header = (
    <div className="inline-flex gap-3 w-full items-center flex-wrap">
      <BackAction />
      <Breadcrumb
        items={[
          { label: 'Pengaturan Produk' },
          { label: isEdit ? code || 'Detail Produk' : 'Tambah Produk' },
        ]}
      />
    </div>
  );

  if (isEdit && !product) {
    return (
      <section className="flex! flex-col gap-6 overflow-auto mb-10">
        {header}
        <div className="shadowCard p-6">
          <p className="text-center">
            {isLoading ? 'Memuat...' : isError ? 'Gagal memuat produk' : 'Produk tidak ditemukan'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      {header}

      <div className="shadowCard flex flex-col">
        <div className="w-full p-3 lg:p-6 flex flex-wrap gap-3 justify-between">
          <div className="shrink-0">
            <p className="title1bold capitalize">{isEdit ? 'Detail Produk' : 'Tambah Produk'}</p>
            <p className="body1 text-text-grey">{code}</p>
          </div>
          {product && (
            <BadgeCourier
              statusCourier={PRODUCT_STATUS_BADGE[product.availability]}
              label={product.availability_label}
            />
          )}
        </div>

        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />

        <section className="w-full p-3 lg:p-6 flex!  flex-wrap gap-6 justify-between">
          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="shrink-0">
              <p className="title2bold capitalize">Informasi Produk</p>
              <p className="body2 text-text-grey">Detail produk dan data utama penjualan</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-6 lg:grid-cols-3 md:grid-cols-3">
              <DetailForm
                direction="col"
                label="ID"
                inputType="text"
                disabled
                value={product?.sku ?? ''}
                placeholder="Dibuat otomatis"
              />
              <DetailForm
                direction="col"
                label="BAHASA"
                inputType="text"
                placeholder="Masukkan bahasa (Cth. Indonesia)"
                value={form.language}
                onChange={set('language')}
              />
              <DetailForm
                direction="col"
                label="HARGA ASLI (Rp)"
                inputType="text"
                placeholder="7.797.000"
                required
                value={form.price ? formatPrice(Number(form.price)) : ''}
                onChange={set('price', digits)}
              />
              <DetailForm
                direction="col"
                label="NAMA BUKU"
                inputType="text"
                placeholder="Masukkan nama buku"
                required
                value={form.title}
                onChange={set('title')}
              />
              <DetailForm
                direction="col"
                label="PANJANG (CM)"
                inputType="text"
                placeholder="23,4"
                required
                value={form.length_cm}
                onChange={set('length_cm')}
              />
              <DetailForm
                direction="col"
                label="DISKON (%)"
                inputType="text"
                placeholder="0"
                value={form.discount_percent}
                onChange={set('discount_percent')}
              />
              <DetailForm
                direction="col"
                label="NOMOR ISBN"
                inputType="text"
                placeholder="Contoh: 979-11-221489-1-6"
                disabled={isBundle}
                value={form.isbn}
                onChange={set('isbn')}
              />
              <DetailForm
                direction="col"
                label="LEBAR (CM)"
                inputType="text"
                placeholder="26,8"
                required
                value={form.width_cm}
                onChange={set('width_cm')}
              />
              <DetailForm
                direction="col"
                label="HARGA AKHIR"
                inputType="text"
                disabled
                value={`Rp ${formatPrice(finalPrice(form.price, form.discount_percent))}`}
              />
              <DetailForm
                direction="col"
                label="KATEGORI"
                inputType="select"
                required={!isBundle}
                disabled={isBundle}
                value={form.category_code}
                onChange={set('category_code')}
                optionSelect={[
                  { value: '', label: 'Pilih Kategori' },
                  ...(categories?.rows ?? []).map((c) => ({ value: c.code, label: c.name })),
                ]}
              />
              <DetailForm
                direction="col"
                label="TINGGI/TEBAL (CM)"
                inputType="text"
                placeholder="0,9"
                required
                value={form.thickness_cm}
                onChange={set('thickness_cm')}
              />
              <DetailForm
                direction="col"
                label={isBundle ? 'JUMLAH STOK TERSEDIA (DARI BUKU)' : 'JUMLAH STOK TERSEDIA'}
                inputType="text"
                placeholder="Masukkan jumlah stok"
                required={!isBundle}
                disabled={isBundle}
                value={form.stock}
                onChange={set('stock', digits)}
              />
              <DetailForm
                direction="col"
                label="PENULIS"
                inputType="text"
                placeholder="Masukkan nama penulis"
                disabled={isBundle}
                value={form.author}
                onChange={set('author')}
              />
              <DetailForm
                direction="col"
                label="BERAT (GR)"
                inputType="text"
                placeholder="430"
                required
                value={form.weight_grams}
                onChange={set('weight_grams', digits)}
              />
              <DetailForm
                direction="col"
                label="STATUS"
                inputType="select"
                required
                value={form.status}
                onChange={set('status')}
                optionSelect={[{ value: '', label: 'Pilih status' }, ...PRODUCT_STATUS_OPTIONS]}
              />
              <DetailForm
                styleTextArea="h-[200px]"
                className="grid lg:col-span-3 md:col-span-3 sm:col-span-2 col-span-1"
                direction="col"
                label="DESKRIPSI"
                inputType="textarea"
                placeholder="Tuliskan deskripsi lengkap mengenai buku ini..."
                value={form.description}
                onChange={set('description')}
              />

              <PhotoUploader
                className="grid lg:col-span-3 md:col-span-3 sm:col-span-2 col-span-1"
                photos={photos}
                onAdd={handleAddPhotos}
                onRemove={handleRemovePhoto}
                maxPhotos={product?.max_images ?? MAX_PHOTOS}
                accept={PHOTO_TYPES}
                disabled={busy}
              />
            </div>
          </article>
        </section>

        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />

        <div className="p-6 w-full inline-flex  gap-3 justify-between items-center flex-wrap">
          <LogList
            logs={[
              { label: 'Pendaftar', value: product?.created_by?.name ?? '-' },
              { label: 'Waktu Daftar', value: product ? formatStamp(product.created_at) : '-' },
            ]}
          />

          <div className="gap-3 inline-flex flex-wrap w-full flex-1 md:justify-end lg:justify-end sm:justify-end basis-[300px]">
            <Button
              className="btn-outline-red w-full sm:w-fit lg:w-fit md:w-fit  shrink-0"
              disabled={busy}
              onClick={() => navigate('/dashboard/product')}
            >
              <span>Batal</span>
            </Button>
            {isEdit && (
              <Button
                className="btn-fill-red w-full sm:w-fit lg:w-fit md:w-fit  shrink-0"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                <span>Hapus</span>
              </Button>
            )}
            <Button
              className="btn-fill-blue w-full sm:w-fit lg:w-fit md:w-fit  shrink-0"
              disabled={busy}
              onClick={handleSave}
            >
              <span>{isEdit ? 'Simpan' : 'Tambah'}</span>
            </Button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Hapus Produk?"
        message="Apakah Anda yakin ingin menghapus produk ini? Tindakan ini tidak dapat dibatalkan."
        confirmLabel="Ya, Hapus"
        variant="danger"
        loading={deleteProduct.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </section>
  );
};

export default ProductDetailManagement;
